<template>
  <div class="bank-transfer-container">
    <!-- 交易列表 -->
    <div class="table-wrapper">
      <div class="table-header">
        <div class="header-cell" style="flex: 1.5">{{ $t('_m._jyh') }}</div>
        <div class="header-cell" style="flex: 1">{{ $t('_m._jyje') }}</div>
        <div class="header-cell" style="flex: 1.5">{{ $t('_m._yhmz') }}</div>
        <div class="header-cell" style="flex: 2">{{ $t('_m._yhdz') }}</div>
        <div class="header-cell" style="flex: 1.2">{{ $t('_cb._sj') }}</div>
      </div>

      <!-- 列表项 -->
      <div
        v-for="item in transactions"
        :key="item.id"
        class="table-row"
      >
        <div class="row-cell" style="flex: 1.5">
          <div class="transfer-info">
            <span class="transfer-no">{{ item.transfer_no }}</span>
          </div>
        </div>

        <div class="row-cell" style="flex: 1">
          <span class="amount income">+${{ item.amount }}</span>
        </div>

        <div class="row-cell" style="flex: 1.5">
          <span class="bank-name">{{ item.bank_name || '-' }}</span>
        </div>

        <div class="row-cell" style="flex: 2">
          <div class="account-cell">
            <span class="account-text">{{ item.bank_account || '-' }}</span>
          </div>
        </div>

        <div class="row-cell" style="flex: 1.2">
          <div class="time-info">
            <div class="time-date">{{ formatDate(item.create_time) }}</div>
            <div class="time-hour">{{ formatTime(item.create_time) }}</div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-if="transactions.length === 0 && !loading" class="empty-state">
        <i class="fas fa-building"></i>
        <p>{{ $t('_cb._zwjl') }}</p>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ $t('_a._jzz') }}</p>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-bar" v-if="transactions.length > 0">
      <button class="page-btn" :disabled="page === 1" @click="onPage(page - 1)">
        <LeftOutlined />
      </button>
      <span class="page-info">{{ $t('_cb._dny', { a: page, b: totalPages }) }}</span>
      <button class="page-btn" :disabled="page === totalPages" @click="onPage(page + 1)">
        <RightOutlined />
      </button>
    </div>
  </div>
</template>

<script>
import { RightOutlined, LeftOutlined } from '@ant-design/icons-vue'

export default {
  name: 'BankTransferRecords',
  components: {
    RightOutlined,
    LeftOutlined
  },
  data() {
    return {
      transactions: [],
      page: 1,
      pageSize: 10,
      total: 0,
      loading: false
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.getList()
  },
  methods: {
    getList() {
      this.loading = true
      const hide = this.$message.loading({ content: this.$t('_a._jzz'), key: 'msg' })

      this.$api.userWallet
        .getBankReceiveList({
          page: this.page,
          size: this.pageSize,
          search: ''
        })
        .then((res) => {
          hide()
          this.transactions = res.data?.data || []
          this.total = res.data?.total || 0
        })
        .catch(() => {
          hide()
          this.transactions = []
          this.total = 0
        })
        .finally(() => {
          this.loading = false
        })
    },

    onPage(page) {
      if (page < 1 || page > this.totalPages) return
      this.page = page
      this.getList()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString('zh-CN')
    },

    formatTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
/* ============================================================
   银行转账记录 - 暗色主题
   ============================================================ */

.bank-transfer-container {
  background: transparent;
  color: #e8edf2;
}

/* ============================================================
   表格
   ============================================================ */
.table-wrapper {
  background: #0f1419;
  border-radius: 14px;
  border: 1px solid #1a1f26;
  overflow: hidden;
}

/* 表头 */
.table-header {
  display: flex;
  padding: 12px 20px;
  background: #0a0d0f;
  border-bottom: 1px solid #1a1f26;
}

.header-cell {
  color: #5a6672;
  font-size: 15px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0 6px;
}

/* 行 */
.table-row {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid #141a20;
  transition: background 0.2s;
}

.table-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.table-row:last-child {
  border-bottom: none;
}

.row-cell {
  display: flex;
  align-items: center;
  padding: 0 6px;
  min-height: 44px;
  word-break: break-word;
}

/* ============================================================
   交易号
   ============================================================ */
.transfer-no {
  color: #e8edf2;
  font-size: 14px;
  font-weight: 500;
  font-family: monospace;
  letter-spacing: 0.3px;
}

/* ============================================================
   金额
   ============================================================ */
.amount {
  font-size: 15px;
  font-weight: 600;
}

.amount.income {
  color: #26a69a;
}

/* ============================================================
   银行名称
   ============================================================ */
.bank-name {
  color: #e8edf2;
  font-size: 14px;
}

/* ============================================================
   银行账号
   ============================================================ */
.account-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.account-text {
  color: #8a949f;
  font-size: 14px;
  font-family: monospace;
  letter-spacing: 0.3px;
}

/* ============================================================
   时间
   ============================================================ */
.time-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.time-date {
  color: #e8edf2;
  font-size: 13px;
}

.time-hour {
  color: #5a6672;
  font-size: 12px;
}

/* ============================================================
   空状态
   ============================================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #5a6672;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* ============================================================
   加载状态
   ============================================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #5a6672;
}

.loading-state i {
  font-size: 32px;
  margin-bottom: 10px;
  color: #e50914;
}

.loading-state p {
  font-size: 14px;
  margin: 0;
}

/* ============================================================
   分页
   ============================================================ */
.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px 0 0;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #1a1f26;
  border-radius: 8px;
  background: #0f1419;
  color: #8a949f;
  cursor: pointer;
  transition: all 0.25s;
}

.page-btn:hover:not(:disabled) {
  background: #181e24;
  border-color: #2a3138;
  color: #e8edf2;
}

.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.page-info {
  color: #8a949f;
  font-size: 14px;
}

/* ============================================================
   响应式
   ============================================================ */
@media (max-width: 820px) {
  .table-header {
    display: none;
  }

  .table-row {
    flex-direction: column;
    align-items: stretch;
    padding: 14px 16px;
    gap: 8px;
  }

  .row-cell {
    padding: 0;
    min-height: auto;
  }

  /* 调整顺序 */
  .row-cell[style*="flex: 1.5"]:first-child {
    order: 1;
  }
  .row-cell[style*="flex: 1"] {
    order: 2;
  }
  .row-cell[style*="flex: 1.5"]:nth-child(3) {
    order: 3;
  }
  .row-cell[style*="flex: 2"] {
    order: 4;
  }
  .row-cell[style*="flex: 1.2"] {
    order: 5;
  }

  .row-cell {
    display: flex;
    justify-content: space-between;
    padding: 4px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  }

  .row-cell::before {
    content: attr(data-label);
    color: #5a6672;
    font-size: 13px;
    font-weight: 500;
  }

  .row-cell:last-child {
    border-bottom: none;
  }

  .transfer-no,
  .bank-name,
  .account-text {
    font-size: 14px;
  }

  .amount {
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .table-row {
    padding: 12px 14px;
    gap: 6px;
  }

  .transfer-no {
    font-size: 13px;
  }

  .amount {
    font-size: 14px;
  }

  .bank-name {
    font-size: 13px;
  }

  .account-text {
    font-size: 13px;
  }

  .time-date {
    font-size: 12px;
  }

  .time-hour {
    font-size: 11px;
  }

  .page-btn {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .page-info {
    font-size: 13px;
  }
}
</style>